import React, { lazy, Suspense, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import ModalShell from "./ModalShell";
import ProfileInfoTabs from "./ProfileInfoTabs";
import ProfileInfoEditorTabs from "./ProfileInfoEditorTabs";
import TabsList from "./TabsList";
import TabLabelWithCount from "./TabLabelWithCount";
import { useParticipantContactLogs } from "../shared/hooks/useParticipantContactLogs";
import {
  ACCOUNT_SIGNUP_FORM_CLASS,
  ACCOUNT_SIGNUP_MODAL_MAX_WIDTH,
} from "../shared/utils/accountSignupForm";
import "./ProfileInfoModal.css";

const ProfileInfoCommunicationTab = lazy(() => import("./ProfileInfoCommunicationTab"));

const TAB_OVERVIEW = "overview";
const TAB_COMMUNICATION = "communication";

function buildProfileInfoModalTitle({ firstName, lastName } = {}) {
  const fullName = [firstName, lastName]
    .map((v) => (v != null ? String(v).trim() : ""))
    .filter((s) => s && s !== "—")
    .join(" ");
  return fullName ? `Profile info (${fullName})` : "Profile info";
}

/**
 * Shared profile info modal — same shell width and form typography as account registration.
 * @param {boolean} [editable=false] - when true, show CRUD editor (profile icon menu)
 * @param {string} [participantUserId] - staff-only: participant user id enables Communication & Notes
 * @param {string} [relatedEventId] - optional event to attach to new notes (Sign-ups context)
 */
function ProfileInfoModal({
  open,
  onClose,
  editable = false,
  participantUserId,
  relatedEventId,
  /** When opening for follow-up queue, pass "communication" to land on Notes. */
  initialTab,
  ...profileProps
}) {
  const { userInfo } = useAuth();
  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);
  const showCommunication = !editable && !!(participantUserId && String(participantUserId).trim());
  const logsEnabled = !!(open && showCommunication);

  const { logs, loading, error, setError, reload, count } = useParticipantContactLogs({
    accountId: userInfo?.account_id,
    participantUserId,
    enabled: logsEnabled,
  });

  useEffect(() => {
    if (!open) {
      setActiveTab(TAB_OVERVIEW);
      return;
    }
    const wantComm =
      showCommunication &&
      initialTab != null &&
      String(initialTab).trim().toLowerCase() === TAB_COMMUNICATION;
    setActiveTab(wantComm ? TAB_COMMUNICATION : TAB_OVERVIEW);
  }, [open, participantUserId, initialTab, showCommunication]);

  const overviewContent = editable ? (
    <ProfileInfoEditorTabs {...profileProps} titleId="profile-modal-title" hidePageChrome />
  ) : (
    <ProfileInfoTabs
      {...profileProps}
      titleId="profile-modal-title"
      hidePageChrome
    />
  );

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={buildProfileInfoModalTitle(profileProps)}
      maxWidth={ACCOUNT_SIGNUP_MODAL_MAX_WIDTH}
      zIndex={1050}
      className="ProfileInfoModal-shell"
      bodyClassName={ACCOUNT_SIGNUP_FORM_CLASS}
      footer={
        <div className="modal-actions">
          <button type="button" className="secondary" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      {showCommunication ? (
        <>
          <div className="tabs-bar tabs-bar-underline ProfileInfoModal-tabs">
            <TabsList role="tablist" aria-label="Profile info sections">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === TAB_OVERVIEW}
                className={`tab-btn ${activeTab === TAB_OVERVIEW ? "active" : ""}`}
                onClick={() => setActiveTab(TAB_OVERVIEW)}
              >
                Overview
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === TAB_COMMUNICATION}
                className={`tab-btn ${activeTab === TAB_COMMUNICATION ? "active" : ""}`}
                onClick={() => setActiveTab(TAB_COMMUNICATION)}
              >
                <TabLabelWithCount label="Communication & Notes" count={count} />
              </button>
            </TabsList>
          </div>
          {activeTab === TAB_COMMUNICATION ? (
            <Suspense fallback={<p className="empty-message">Loading…</p>}>
              <ProfileInfoCommunicationTab
                key={String(participantUserId)}
                participantUserId={participantUserId}
                relatedEventId={relatedEventId}
                logs={logs}
                loading={loading}
                error={error}
                setError={setError}
                reload={reload}
              />
            </Suspense>
          ) : (
            overviewContent
          )}
        </>
      ) : (
        overviewContent
      )}
    </ModalShell>
  );
}

export default ProfileInfoModal;
